import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Smile, Meh, Frown, SmilePlus, Angry, Battery, Zap, Coffee, ArrowLeft } from "lucide-react";
import { format } from "date-fns";

const DashboardMoodTracker = () => {
  const [mood, setMood] = useState<number>(3);
  const [energy, setEnergy] = useState<number>(3);
  const [notes, setNotes] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [moodHistory, setMoodHistory] = useState<any[]>([]);
  const { toast } = useToast();

  const moodOptions = [
    { score: 1, icon: Angry, label: "Terrible", color: "text-red-500" },
    { score: 2, icon: Frown, label: "Bad", color: "text-orange-500" },
    { score: 3, icon: Meh, label: "Okay", color: "text-yellow-500" },
    { score: 4, icon: Smile, label: "Good", color: "text-green-500" },
    { score: 5, icon: SmilePlus, label: "Excellent", color: "text-blue-500" },
  ];

  const energyLevels = [
    { score: 1, icon: Battery, label: "Exhausted" },
    { score: 2, icon: Battery, label: "Tired" },
    { score: 3, icon: Coffee, label: "Normal" },
    { score: 4, icon: Zap, label: "Energized" },
    { score: 5, icon: Zap, label: "Pumped" },
  ];

  const availableTags = [
    "Productive", "Focused", "Stressed", "Happy", "Anxious", 
    "Motivated", "Creative", "Tired", "Inspired", "Overwhelmed"
  ];

  useEffect(() => {
    fetchMoodHistory();
  }, []);

  const fetchMoodHistory = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("mood_entries")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(10);

    if (error) {
      console.error("Error fetching mood history:", error);
    } else {
      setMoodHistory(data || []);
    }
  };

  const handleSubmit = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const selectedMood = moodOptions.find(m => m.score === mood);
    
    const { error } = await supabase
      .from("mood_entries")
      .insert({
        user_id: user.id,
        mood_score: mood,
        mood_type: selectedMood?.label || "Okay",
        energy_level: energy,
        notes: notes || null,
        tags: selectedTags.length > 0 ? selectedTags : null,
      });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to save mood entry",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Mood Logged!",
        description: "Your mood has been recorded",
      });
      setNotes("");
      setSelectedTags([]);
      fetchMoodHistory();
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const getMoodIcon = (score: number) => {
    const mood = moodOptions.find(m => m.score === score);
    if (!mood) return null;
    const Icon = mood.icon;
    return <Icon className={`h-6 w-6 ${mood.color}`} />;
  };

  const getAverageMood = () => {
    if (moodHistory.length === 0) return 0;
    const sum = moodHistory.reduce((acc, entry) => acc + entry.mood_score, 0);
    return (sum / moodHistory.length).toFixed(1);
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Link to="/dashboard">
        <Button variant="ghost" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </Link>

      <h1 className="text-3xl font-bold mb-8">Mood Tracker</h1>

      {/* Today's Mood Entry */}
      <Card className="p-6 mb-8">
        <h2 className="text-xl font-semibold mb-6">How are you feeling today?</h2>
        
        <div className="space-y-6">
          {/* Mood Selection */}
          <div>
            <label className="text-sm font-medium mb-3 block">Your Mood</label>
            <div className="flex justify-between gap-2">
              {moodOptions.map(({ score, icon: Icon, label, color }) => (
                <button
                  key={score}
                  onClick={() => setMood(score)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    mood === score
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <Icon className={`h-8 w-8 ${color}`} />
                  <span className="text-xs font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Energy Level */}
          <div>
            <label className="text-sm font-medium mb-3 block">Energy Level</label>
            <div className="flex justify-between gap-2">
              {energyLevels.map(({ score, icon: Icon, label }) => (
                <button
                  key={score}
                  onClick={() => setEnergy(score)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    energy === score
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <Icon className="h-6 w-6 text-primary" />
                  <span className="text-xs">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="text-sm font-medium mb-3 block">How do you feel? (optional)</label>
            <div className="flex flex-wrap gap-2">
              {availableTags.map(tag => (
                <Badge
                  key={tag}
                  variant={selectedTags.includes(tag) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium mb-2 block">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What's on your mind?"
              rows={3}
            />
          </div>

          <Button onClick={handleSubmit} className="w-full">
            Log Mood
          </Button>
        </div>
      </Card>

      {/* Mood Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="p-6">
          <p className="text-sm text-muted-foreground mb-2">Average Mood</p>
          <p className="text-3xl font-bold">{getAverageMood()}/5</p>
        </Card>
        <Card className="p-6">
          <p className="text-sm text-muted-foreground mb-2">Total Entries</p>
          <p className="text-3xl font-bold">{moodHistory.length}</p>
        </Card>
        <Card className="p-6">
          <p className="text-sm text-muted-foreground mb-2">Current Streak</p>
          <p className="text-3xl font-bold">🔥 0 days</p>
        </Card>
      </div>

      {/* Mood History */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Entries</h2>
        <div className="space-y-4">
          {moodHistory.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No mood entries yet. Start tracking your mood today!
            </p>
          ) : (
            moodHistory.map((entry) => (
              <div
                key={entry.id}
                className="flex items-start gap-4 p-4 rounded-lg bg-muted/50"
              >
                <div className="flex-shrink-0">
                  {getMoodIcon(entry.mood_score)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{entry.mood_type}</span>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(entry.created_at), "MMM d, yyyy")}
                    </span>
                  </div>
                  {entry.tags && entry.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {entry.tags.map((tag: string) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {entry.notes && (
                    <p className="text-sm text-muted-foreground">{entry.notes}</p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

export default DashboardMoodTracker;